import React, { useState } from 'react';
import { Button, Card, Col, Container, Row } from 'reactstrap';
import pivot from 'data-pivot';

import GreenTag from "../../base/data/GreenTag";
import { assert } from "../../base/utils/assert";
import { bytes, ellipsize, space, yessy } from '../../base/utils/miscutils';
import { isTester } from '../../base/Roles';
import CopyButton from '../../base/components/CopyButton';
import ShareWidget from '../../base/components/ShareWidget';
import { gdi, label, getDynamicImpactDebits } from './greenTagPageUtils';
import { A } from '../../base/plumbing/glrouter';
import { LoginWidgetEmbed } from '../../base/components/LoginWidget';
import Misc from '../MiscOverrides';
import MailTo from '../../base/components/MailTo';
import XId from '../../base/data/XId';
import Login from '../../base/youagain';
import C from '../../C';
import DevOnly from '../../base/components/DevOnly';
import SimpleTable from '../../base/components/SimpleTable';
import { getDataLogData } from '../../base/plumbing/DataLog';
import ErrAlert from '../../base/components/ErrAlert';
import { noteCount } from '../Notes';


/** Maps item types to green ad tag prop, display name, and portal editor page */
const abcProps = {
	[C.TYPES.Agency]: { member: 'agencyId', legend: 'Agency', page: 'agency' },
	[C.TYPES.Advertiser]: { member: 'vertiser', legend: 'Brand', page: 'advertiser' },
	[C.TYPES.Campaign]: { member: 'campaign', legend: 'Campaign', page: 'campaign' }
};


/** Agency/Brand/Campaign label for a given tag
 * @param {Object} p
 * @param {!String} p.type Agency|Campaign|Advertiser
 * @param {!GreenTag} p.tag
*/
export function ABC({type, tag, noLink, copy, noShare}) {
	assert(abcProps[type]);
	GreenTag.assIsa(tag);
	const {member, legend, page} = abcProps[type];
	const id = tag[member];
	const hasShare = id && !noShare;

	const name = label(type, id);
	const copyText = `${legend}: ${name} (${id})`;
	const labelText = <div title={`${name} (${id})`} className="name text-truncate">{name}</div>;

	return <div className={space('abc', page, hasShare && 'has-share')}>
		<div className="legend-buttons">
			{legend}
			{copy && <> <CopyButton size="xs" text={copyText} title="Copy name and ID" /></>}
			{hasShare && <> <ShareWidget item={gdi(type, id).value} title={`Share this ${legend.toLowerCase()}`} size="xs" hasButton /></>}
		</div>
		<div className="name">
			{(isTester() && !noLink) ? (
				<A href={id ? `/#${page}/${id}` : null}>{labelText}</A>
			) : labelText}
		</div>
	</div>;
}


export function GreenLogin() {
	return (
		<Card body id="green-login-card" className="m-4">
			<Container>
				<Row>
					<Col className="decoration flex-center" xs="12" sm="4">
						<img className="stamp" src="/img/green/gl-carbon-neutral.svg" alt="Good-Loop &quot;carbon neutral&quot; stamp" />
					</Col>
					<Col className="form" xs="12" sm="8">
						<img className="gl-logo my-4" src="/img/gl-logo/rectangle/logo-name.svg" alt="Good-Loop logo" />
						<p className="text-center my-4">
							Understand the carbon footprint of your advertising and<br />
							discover your offsetting and climate-positive successes
						</p>
						<LoginWidgetEmbed verb="login" canRegister={false} />
					</Col>
				</Row>
			</Container>
		</Card>
	);
}

/**
 * Indicates measurement status. Will show:
 * - a red light for "Problem measuring"
 * - a yellow light for "Can't measure yet, creative URL is placeholder"
 * - an hourglass for "Ready to measure"
 * - a green light for "Measured OK".
 */
function TagStatusMarker({tag, className, ...props}) {
	let icon = '🟢';
	let title = 'Creative measured!';
	if (tag.creativeURL?.match('example.com')) {
		title = 'Placeholder URL';
		icon = '🟠';
	} else if (!tag.weight) {
		title = 'Pending creative measurement';
		icon = '⏳';
	} else if (tag.weight < 0) {
		title = 'Problem with Creative URL';
		icon = '🔴';
	}
	return <span className={space('tag-marker tag-status-marker', className)} {...props} title={title}>{icon}</span>;
}


/**
 * Indicates when a tag has attached notes. Shows a number in a speech bubble, or nothing.
 */
function TagNotesMarker({tag}) {
	const count = noteCount(tag);
	if (!count) return null;

	return <div className="tag-marker tag-notes-marker">
		{/*<img src="/img/speech-bubble.png" style={{width: '50px'}} alt="" />*/}
		<span title={`${noteCount(tag)} notes`}>{noteCount(tag)}</span>
	</div>;
}


/** Tag name, creator and timestamp */
export function TagHeaderLine({tag}) {
	const tagName = tag.name || '(no name)';
	const created = new Date(tag.created);

	return <>
		<h4 className="tag-name text-truncate" title={tagName}>{ellipsize(tagName, 45)}</h4>
		<p className="tag-created mb-0">
			<span title={`Tag created ${created.toString()}`}>
				<Misc.RoughDate date={created} />
			</span> <span title={XId.id(tag.oxid || '@email')}>
				by <MailTo email={tag.oxid && XId.id(tag.oxid)}>{XId.prettyName(tag.oxid || 'unknown@email')}</MailTo>
			</span>
		</p>
		<div className="tag-markers">
			<TagNotesMarker tag={tag} />
			<TagStatusMarker tag={tag} />
		</div>
	</>;
}


/** We have a tag ID, but the tag hasn't been loaded (yet?) - display an appropriate message. */
export function NoTagYet({ pvTag, tagId }) {
	// Just waiting for response from server?
	if (!pvTag.resolved) return <Misc.Loading text="Loading tag..." />;

	if (tagId) {
		if (pvTag.error && pvTag.error.message.match(/^403/)) { // Not yours!
			if (Login.isLoggedIn()) {
				return <>Tag "{tagId}" doesn't belong to you.</>;
			}
			return <>You must be logged in to view and edit your tags.</>;
		}
		return <>Couldn't find tag "{tagId}".</>; // Probably just a 404.
	}

	return <>Bad tag ID: "{tagId}"</>; // Blank/falsy tag ID? Shouldn't happen, but...
}


/**
 * d=green pixel
 */
function TagDetailsStats({ tag }) {
	let [open, setOpen] = useState();
	if (!open) return <Button onClick={e => setOpen(true)}>Fetch Stats</Button>;
	let dataspace = 'green', t = 'pixel', campaign = tag.campaign;
	let start = tag.created;
	// NB: older 2021 tags use trk :(
	// dataspace='trk'; t='pxl_green'; start='6 months ago';
	let breakdowns = ['pub', 'mbl'];
	let q = 'evt:' + t + ' campaign:' + campaign; // TODO use searchquery
	const pvData = getDataLogData({ q, breakdowns, start, dataspace });
	if (!pvData.resolved) return <Misc.Loading />;
	if (pvData.error) return <ErrAlert error={pvData.error} />;
	// https://lg.good-loop.com/data?dataspace=trk&q=evt:pxl_green%20campaign:30snsi&start=2021-11-01
	const data = pvData.value;
	// HACK smash it into a table
	let count4pub = pivot(data, 'by_pub.buckets.$bi.{key, count}', '$key.$count');
	let count4mbl = pivot(data, 'by_mbl.buckets.$bi.{key, count}', '$key.$count');
	count4mbl.unsetDevice = count4mbl.unset;
	delete count4mbl.unset;
	count4pub.unsetPublisher = count4pub.unset;
	delete count4pub.unset;
	let dataObject = Object.assign({ total: data.allCount }, count4mbl, count4pub);
	return <SimpleTable dataObject={dataObject} hasCsv />;
}

/** Maps properties of a Green Tag object to filter modes on the Green Dashboard */
const memberToDashFilter = { id: 'tag', campaign: 'campaign', vertiser: 'brand', agencyId: 'agency' };

/**
 * 
 * @param {?string} tab "recommendation/creative"
 * @returns 
 */
function DashboardLink({tag, field, children, tab}) {
	const filter = memberToDashFilter[field];
	const itemId = tag[field];
	return (
		<A disabled={!itemId} href={`${C.HTTPS}://${C.SERVER_TYPE}my.good-loop.com/greendash${tab? "/"+tab : ""}?${filter}=${itemId}`}>
			{children || filter}
		</A>
	);
}


/**
 * Summary of tag information
 * NB: does NOT include the Tag export
 * 
 * @param {Object} p
 * @param {GreenTag} p.tag
 * @param {boolean} [p.editing]
 * @returns 
 */
export function TagDetails({tag, editing}) {
	// offset info
	let pvimpds = tag.campaign && getDynamicImpactDebits(tag.campaign, C.TYPES.Campaign);
	let impds = pvimpds?.value;
	let offsetEl = (
		<div className="offset mb-1">
			<small>Offset:</small><br/>
			{!yessy(impds) ? 'none set' : impds.map(impd => impd.impact.name).join(', ')}
		</div>
	);

	const campaignLink = <DashboardLink tag={tag} field="campaign" />;
	const tagLink = <DashboardLink tag={tag} field="id" />;
	const recsLink = <DashboardLink tag={tag} field="id" tab="recommendation/creative">recommendations</DashboardLink>;

	return <>
		<div className="clearfix mb-2 position-relative">
			<TagHeaderLine tag={tag} />
		</div>
		<div className="flex-row justify-content-between">
			{!editing && <ABC type={C.TYPES.Agency} tag={tag} noShare copy/>}
			<ABC type={C.TYPES.Advertiser} tag={tag} copy/>
			<ABC type={C.TYPES.Campaign} tag={tag} copy/>
			{editing && offsetEl}
		</div>
		{!editing && <>
			{offsetEl}
			<DevOnly><TagDetailsStats tag={tag} /></DevOnly>
			<p>View dashboard for {campaignLink} or {tagLink}</p>
			{tag.weight && <p>Creative weight: {bytes(tag.weight)} ({recsLink})
			</p>}
		</>}
	</>;
}
